import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const STATS = [
  {
    value: "£5-15",
    label: "Video ad CPL",
    detail:
      "Cost per lead from social media video ads. The lowest of any property marketing channel in 2026.",
  },
  {
    value: "£15-50",
    label: "Portal CPL",
    detail:
      "Cost per enquiry from Rightmove or Zoopla. High visibility, but you are competing with every other agent in the area.",
  },
  {
    value: "£30-150",
    label: "Print/leaflet CPL",
    detail:
      "Newspaper ads and leaflet drops still have a place, but the cost per measurable lead is significantly higher.",
  },
  {
    value: "3-5x",
    label: "Video ads ROI multiplier",
    detail:
      "Agents running video ads on social media report three to five times more leads per pound compared to traditional channels.",
  },
]

type ChannelRow = {
  channel: string
  monthlyCost: string
  cpl: string
  measurability: string
  notes: string
}

const CHANNEL_DATA: ChannelRow[] = [
  {
    channel: "Rightmove",
    monthlyCost: "£1,000+/month",
    cpl: "£15-40",
    measurability: "Good",
    notes:
      "Essential for most agents, but costs have risen sharply. Limited differentiation between listings.",
  },
  {
    channel: "Zoopla",
    monthlyCost: "£400+/month",
    cpl: "£20-50",
    measurability: "Good",
    notes:
      "Smaller audience than Rightmove. Good supplementary portal, especially in London and the South East.",
  },
  {
    channel: "Newspaper ads",
    monthlyCost: "£200-500/ad",
    cpl: "£50-150",
    measurability: "Poor",
    notes:
      "Declining readership. Difficult to track ROI. Still valued by older vendors as a credibility signal.",
  },
  {
    channel: "Leaflet drops",
    monthlyCost: "£300-600 per 10k",
    cpl: "£30-80",
    measurability: "Poor",
    notes:
      "Useful for prospecting and brand awareness in a farm area. Response rates are typically 0.5-2%.",
  },
  {
    channel: "For-sale boards",
    monthlyCost: "£30-80 each",
    cpl: "Hard to measure",
    measurability: "Very poor",
    notes:
      "Effective as a local brand signal. Almost impossible to attribute leads directly.",
  },
  {
    channel: "Social video ads",
    monthlyCost: "£100-300/month",
    cpl: "£5-15",
    measurability: "Excellent",
    notes:
      "Lowest CPL of any channel. Precise targeting by location, demographics, and interests. Full attribution tracking.",
  },
  {
    channel: "Google Ads",
    monthlyCost: "£500+/month",
    cpl: "£20-60",
    measurability: "Excellent",
    notes:
      "High-intent clicks, but competitive keywords drive costs up. Works best for valuation landing pages.",
  },
]

type QualityRow = {
  metric: string
  videoAds: string
  traditional: string
}

const QUALITY_DATA: QualityRow[] = [
  {
    metric: "Lead intent level",
    videoAds: "Medium to high (engaged viewers)",
    traditional: "Varies widely (passive exposure)",
  },
  {
    metric: "Contact rate",
    videoAds: "15-25% of leads make contact",
    traditional: "5-15% of leads make contact",
  },
  {
    metric: "Valuation booking rate",
    videoAds: "8-15% of enquiries book",
    traditional: "3-8% of enquiries book",
  },
  {
    metric: "Time to first contact",
    videoAds: "Often within hours (mobile)",
    traditional: "Days to weeks (print, boards)",
  },
  {
    metric: "Geographic targeting",
    videoAds: "Precise (postcode, radius)",
    traditional: "Broad (newspaper circulation, leaflet area)",
  },
  {
    metric: "Attribution clarity",
    videoAds: "Full tracking (impressions to conversion)",
    traditional: "Mostly guesswork (ask vendors how they found you)",
  },
  {
    metric: "Brand recall",
    videoAds: "High (video is memorable)",
    traditional: "Moderate (print) to low (leaflets)",
  },
]

type BudgetScenario = {
  budget: string
  portals: string
  videoAds: string
  print: string
  estimatedLeads: string
  bestFor: string
}

const BUDGET_SCENARIOS: BudgetScenario[] = [
  {
    budget: "£500/month",
    portals: "£0 (rely on existing portal subscription)",
    videoAds: "£300 on social video ads",
    print: "£200 on targeted leaflets",
    estimatedLeads: "25-50 leads/month",
    bestFor: "Solo agents, new branches, or agencies testing video marketing",
  },
  {
    budget: "£1,000/month",
    portals: "£400 (Zoopla basic listing)",
    videoAds: "£400 on social video ads",
    print: "£200 on leaflets or local press",
    estimatedLeads: "50-90 leads/month",
    bestFor: "Established high street agents looking to grow market share",
  },
  {
    budget: "£2,000/month",
    portals: "£1,000 (Rightmove + Zoopla)",
    videoAds: "£600 on social video ads",
    print: "£400 on Google Ads or premium print",
    estimatedLeads: "100-160 leads/month",
    bestFor:
      "Multi-branch agencies or agents in competitive urban markets",
  },
]

type RoiRow = {
  channel: string
  spend: string
  estimatedLeads: string
  valuations: string
  instructions: string
}

const ROI_DATA: RoiRow[] = [
  {
    channel: "Social video ads",
    spend: "£1,000",
    estimatedLeads: "65-200",
    valuations: "8-25",
    instructions: "2-6",
  },
  {
    channel: "Rightmove (featured)",
    spend: "£1,000",
    estimatedLeads: "25-65",
    valuations: "3-8",
    instructions: "1-2",
  },
  {
    channel: "Google Ads",
    spend: "£1,000",
    estimatedLeads: "15-50",
    valuations: "2-6",
    instructions: "0.5-1.5",
  },
  {
    channel: "Newspaper advertising",
    spend: "£1,000",
    estimatedLeads: "7-20",
    valuations: "1-3",
    instructions: "0.2-0.8",
  },
  {
    channel: "Leaflet drops",
    spend: "£1,000",
    estimatedLeads: "12-30",
    valuations: "1-4",
    instructions: "0.3-1",
  },
]

type FaqItem = { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: "What is cost per lead (CPL) and why does it matter for estate agents?",
    a: "Cost per lead (CPL) is the total marketing spend divided by the number of enquiries generated. It matters because it reveals which channels actually deliver value. An agent spending £1,000 per month on newspaper ads that produce 10 leads has a CPL of £100, whilst the same budget on video ads might produce 80 leads at £12.50 each. Tracking CPL helps you allocate budget to what works.",
  },
  {
    q: "Are portal listings still worth the cost in 2026?",
    a: "For most agents, portals remain essential because vendors expect to see their property on Rightmove or Zoopla. However, portals should not be your only channel. The cost per lead from portals has increased year on year, and you are competing directly with every other agent. Supplementing portals with social video ads gives you a lower-cost lead source that you control.",
  },
  {
    q: "How do I calculate ROI on video ads for my agency?",
    a: "Track three numbers: total video ad spend, total leads generated, and instructions won from those leads. If you spend £300 per month on video ads and win one additional instruction worth £3,000 in fees, your return is 10x. Most social platforms provide detailed analytics showing impressions, clicks, and enquiries, making this straightforward to measure.",
  },
  {
    q: "Do video ads work for lettings as well as sales?",
    a: "Yes. Video ads are particularly effective for lettings because rental properties need to be filled quickly, and tenants are active on social media. A short property tour video targeted at renters in the right area can fill a vacancy in days rather than weeks. The CPL for lettings video ads is typically even lower than for sales.",
  },
  {
    q: "What budget should I start with for social video ads?",
    a: "Start with £100 to £200 per month. This is enough to boost 10 to 15 listing videos and test what works in your area. Once you see which property types and ad formats generate the best response, increase your budget gradually. Many agents find that £300 to £500 per month on video ads outperforms £1,000 or more on traditional channels.",
  },
  {
    q: "Can I create professional video ads without a videographer?",
    a: "Absolutely. Tools like QuiteRight turn your existing listing photos into scroll-stopping video ads automatically. You paste a listing URL, and the platform generates professional videos ready for social media. At under £1 per video, the cost of content creation is no longer a barrier.",
  },
  {
    q: "How do video ads compare to Google Ads for estate agents?",
    a: "Google Ads capture high-intent searchers (people actively looking for an agent), but competition drives costs up. Video ads on social media reach people earlier in their journey, before they search. The CPL for video ads is typically 2 to 4 times lower than Google Ads, and you build brand awareness simultaneously. The best strategy uses both: video for volume and brand, Google for high-intent conversions.",
  },
  {
    q: "Should I stop spending on print and leaflets entirely?",
    a: "Not necessarily. Print and leaflets still serve a purpose for local brand presence, especially in affluent or rural areas where vendors value a traditional approach. The key is to recognise that these channels have a much higher CPL and lower measurability. Shift the majority of your budget to channels with proven ROI (video ads, portals) and keep a smaller allocation for print if your market expects it.",
  },
]

/* ------------------------------------------------------------------ */
/*  Components                                                         */
/* ------------------------------------------------------------------ */

function ChannelComparisonTable({ rows }: { rows: ChannelRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[800px] text-left">
        <thead>
          <tr className="border-b-2 border-stone">
            <th className="pb-3 pr-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
              Channel
            </th>
            <th className="pb-3 px-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
              Monthly cost
            </th>
            <th className="pb-3 px-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
              CPL (approx.)
            </th>
            <th className="pb-3 px-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
              Measurability
            </th>
            <th className="pb-3 pl-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
              Notes
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className={`border-b border-stone/50 ${
                row.channel === "Social video ads"
                  ? "bg-red/5"
                  : i % 2 === 0
                    ? "bg-cream/30"
                    : ""
              }`}
            >
              <td className="py-3.5 pr-4 text-sm font-medium text-charcoal">
                {row.channel}
                {row.channel === "Social video ads" && (
                  <span className="ml-2 rounded-full bg-red/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red">
                    Lowest CPL
                  </span>
                )}
              </td>
              <td className="py-3.5 px-4 text-sm text-slate">
                {row.monthlyCost}
              </td>
              <td className="py-3.5 px-4 text-sm font-semibold text-charcoal">
                {row.cpl}
              </td>
              <td className="py-3.5 px-4 text-sm text-slate">
                {row.measurability}
              </td>
              <td className="py-3.5 pl-4 text-sm text-slate">{row.notes}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function QualityComparisonTable({ rows }: { rows: QualityRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[600px] text-left">
        <thead>
          <tr className="border-b-2 border-stone">
            <th className="pb-3 pr-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
              Metric
            </th>
            <th className="pb-3 px-4 text-center text-xs font-medium uppercase tracking-[0.15em]">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-red px-3 py-1 text-xs font-semibold text-white">
                Video Ads
              </span>
            </th>
            <th className="pb-3 pl-4 text-center text-xs font-medium uppercase tracking-[0.15em]">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-charcoal px-3 py-1 text-xs font-semibold text-white">
                Traditional
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className={`border-b border-stone/50 ${i % 2 === 0 ? "bg-cream/30" : ""}`}
            >
              <td className="py-3.5 pr-4 text-sm font-medium text-charcoal">
                {row.metric}
              </td>
              <td className="py-3.5 px-4 text-center text-sm text-slate">
                {row.videoAds}
              </td>
              <td className="py-3.5 pl-4 text-center text-sm text-slate">
                {row.traditional}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function FaqAccordion({ items }: { items: FaqItem[] }) {
  return (
    <dl className="divide-y divide-stone">
      {items.map((item, i) => (
        <details key={i} className="group">
          <summary className="flex cursor-pointer items-start justify-between gap-4 py-5 text-left [&::-webkit-details-marker]:hidden">
            <dt className="text-sm font-semibold leading-relaxed text-charcoal">
              {item.q}
            </dt>
            <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-stone text-slate transition-transform duration-200 group-open:rotate-45">
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                className="size-3"
              >
                <path d="M8 3v10M3 8h10" />
              </svg>
            </span>
          </summary>
          <dd className="pb-5 text-sm leading-relaxed text-slate">{item.a}</dd>
        </details>
      ))}
    </dl>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function CostPerLeadComparison({
  meta,
}: {
  meta: SeoMeta
}) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline:
              "Cost Per Lead: Video Ads vs Traditional Property Marketing",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-03-12",
            dateModified: "2026-03-12",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema
          canonical={meta.canonical}
          items={[
            { name: "Guides", path: "/guides" },
            {
              name: "Cost Per Lead: Video Ads vs Traditional Property Marketing",
            },
          ]}
        />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Marketing ROI comparison 2026
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Cost per lead: video ads vs traditional property marketing
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Portals, print, boards, leaflets, Google, social video. Every
            channel costs money. This guide breaks down the real cost per lead
            for each, so you can put your marketing budget where it actually
            delivers instructions. Use our{" "}
            <Link href="/tools/marketing-roi-calculator" className="text-red underline underline-offset-2 hover:text-red/80">marketing ROI calculator</Link>{" "}
            to model your own numbers.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red px-3 py-1 text-xs font-semibold text-white">
              Video Ads
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-charcoal px-3 py-1 text-xs font-semibold text-white">
              Traditional Marketing
            </span>
          </div>
        </div>
      </section>

      <SocialShareBar />

      {/* Stats */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The numbers
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              What does a lead actually cost?
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STATS.map((stat, i) => (
              <ScrollReveal key={i}>
                <div className="rounded-xl border border-stone bg-white p-6 text-center">
                  <p className="font-display text-4xl font-bold text-charcoal">
                    {stat.value}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-red">
                    {stat.label}
                  </p>
                  <p className="mt-3 text-xs leading-relaxed text-slate">
                    {stat.detail}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Deep-dive: Video Ads vs Traditional */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Deep dive
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Video ads vs traditional marketing
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {/* Video Ads card */}
            <ScrollReveal>
              <div className="flex h-full flex-col rounded-xl border-2 border-red/20 bg-red/5 p-6">
                <div className="mb-4 flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-red px-3 py-1 text-xs font-semibold text-white">
                    Video Ads
                  </span>
                  <span className="rounded-full bg-red/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red">
                    Best value
                  </span>
                </div>
                <h3 className="font-display text-lg font-bold text-charcoal">
                  The modern lead engine
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate">
                  Social media video ads on platforms like{" "}
                  <a href="https://www.facebook.com/business/ads" target="_blank" rel="noopener noreferrer" className="text-red underline underline-offset-2 hover:text-red/80">Facebook Ads</a>{" "}
                  combine the lowest cost per lead with
                  the most precise targeting available. You choose exactly who
                  sees your listings: by postcode, age, income bracket, and
                  property interest. Every impression, click, and enquiry is
                  tracked, so you know precisely what your marketing budget
                  delivers. Video content is also inherently more engaging than
                  static images or text, meaning higher click-through rates and
                  better brand recall.
                </p>
                <div className="mt-4 space-y-2 text-sm">
                  <p className="font-medium text-charcoal">Strengths:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Lowest CPL of any channel (£5-15)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Precise geographic and demographic targeting
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Full attribution from impression to instruction
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Builds brand awareness simultaneously
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Scalable: increase budget, increase leads proportionally
                    </li>
                  </ul>
                  <p className="mt-3 font-medium text-charcoal">Considerations:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Requires video content (solved by tools like <Link href="/features" className="text-red underline underline-offset-2 hover:text-red/80">QuiteRight</Link>)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Takes 2-4 weeks to optimise ad performance
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Some vendors still expect portal presence
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>

            {/* Traditional Marketing card */}
            <ScrollReveal>
              <div className="flex h-full flex-col rounded-xl border border-stone bg-white p-6">
                <div className="mb-4">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-charcoal px-3 py-1 text-xs font-semibold text-white">
                    Traditional Marketing
                  </span>
                </div>
                <h3 className="font-display text-lg font-bold text-charcoal">
                  The established approach
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate">
                  Portals like{" "}
                  <a href="https://www.rightmove.co.uk" target="_blank" rel="noopener noreferrer" className="text-red underline underline-offset-2 hover:text-red/80">Rightmove</a>,{" "}
                  <a href="https://www.zoopla.co.uk" target="_blank" rel="noopener noreferrer" className="text-red underline underline-offset-2 hover:text-red/80">Zoopla</a>, and{" "}
                  <a href="https://www.onthemarket.com" target="_blank" rel="noopener noreferrer" className="text-red underline underline-offset-2 hover:text-red/80">OnTheMarket</a>,{" "}
                  plus newspaper advertising, leaflet drops, and for-sale
                  boards have been the backbone of property marketing for
                  decades. These channels still serve a purpose, particularly
                  for vendor confidence and local brand presence. However, costs
                  have increased whilst measurability has stagnated. Most
                  traditional channels offer limited targeting and make it
                  difficult to attribute leads to specific spend.
                </p>
                <div className="mt-4 space-y-2 text-sm">
                  <p className="font-medium text-charcoal">Strengths:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Vendor expectation (portals especially)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Local brand presence (boards, print)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Established audience on major portals
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Credibility signal for older demographics
                    </li>
                  </ul>
                  <p className="mt-3 font-medium text-charcoal">Limitations:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Higher CPL across all channels (£15-150)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Poor measurability (print, boards, leaflets)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Limited targeting: same ad shown to everyone
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Portal costs rising faster than lead volume (compare portals in our <Link href="/guides/zoopla-vs-rightmove-vs-onthemarket" className="text-red underline underline-offset-2 hover:text-red/80">Zoopla vs Rightmove vs OTM guide</Link>)
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Channel-by-channel cost comparison table */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Channel breakdown
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Cost comparison: channel by channel
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base text-slate">
              All figures are UK averages for 2025/2026, including portals
              (<Link href="/integrations/rightmove" className="text-red underline underline-offset-2 hover:text-red/80">Rightmove</Link>,{" "}
              <Link href="/integrations/zoopla" className="text-red underline underline-offset-2 hover:text-red/80">Zoopla</Link>,{" "}
              <Link href="/integrations/onthemarket" className="text-red underline underline-offset-2 hover:text-red/80">OnTheMarket</Link>),{" "}
              <a href="https://ads.google.com" target="_blank" rel="noopener noreferrer" className="text-red underline underline-offset-2 hover:text-red/80">Google Ads</a>, and social video. Your actual CPL will
              vary by location, property type, and campaign quality.
            </p>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <ChannelComparisonTable rows={CHANNEL_DATA} />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Lead quality and conversion table */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Lead quality
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Lead quality and conversion
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base text-slate">
              Cost per lead is only half the picture. The quality of those leads
              determines whether your spend translates into instructions. Here
              is how video ads and traditional channels compare on the metrics
              that matter. For a deeper look at platform selection, see our{" "}
              <Link href="/guides/instagram-vs-facebook-vs-tiktok-property-ads" className="text-red underline underline-offset-2 hover:text-red/80">Instagram vs Facebook vs TikTok comparison</Link>.
            </p>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <QualityComparisonTable rows={QUALITY_DATA} />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Monthly budget scenarios */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Budget planning
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Monthly budget breakdowns
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base text-slate">
              Three realistic scenarios showing how to split your marketing
              budget for maximum lead generation. Each includes a mix of
              channels to balance reach, measurability, and vendor expectations.
            </p>
          </ScrollReveal>

          <div className="mt-12 space-y-6">
            {BUDGET_SCENARIOS.map((scenario, i) => (
              <ScrollReveal key={i}>
                <div
                  className={`rounded-xl p-6 md:p-8 ${
                    i === 1
                      ? "border-2 border-red/20 bg-red/5"
                      : "border border-stone bg-white"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <h3 className="font-display text-2xl font-bold text-charcoal">
                      {scenario.budget}
                    </h3>
                    {i === 1 && (
                      <span className="rounded-full bg-red/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red">
                        Most common
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-slate">{scenario.bestFor}</p>
                  <div className="mt-5 grid gap-4 sm:grid-cols-3">
                    <div className="rounded-lg bg-cream/50 p-4">
                      <p className="text-xs font-medium uppercase tracking-[0.15em] text-slate">
                        Portals
                      </p>
                      <p className="mt-1 text-sm font-semibold text-charcoal">
                        {scenario.portals}
                      </p>
                    </div>
                    <div className="rounded-lg bg-cream/50 p-4">
                      <p className="text-xs font-medium uppercase tracking-[0.15em] text-slate">
                        Video ads
                      </p>
                      <p className="mt-1 text-sm font-semibold text-charcoal">
                        {scenario.videoAds}
                      </p>
                    </div>
                    <div className="rounded-lg bg-cream/50 p-4">
                      <p className="text-xs font-medium uppercase tracking-[0.15em] text-slate">
                        Print / other
                      </p>
                      <p className="mt-1 text-sm font-semibold text-charcoal">
                        {scenario.print}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 rounded-lg bg-charcoal/5 p-4">
                    <p className="text-sm">
                      <span className="font-semibold text-charcoal">
                        Estimated leads:{" "}
                      </span>
                      <span className="font-bold text-red">
                        {scenario.estimatedLeads}
                      </span>
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* The compound effect */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The bigger picture
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              The compound effect of video
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 space-y-6">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Brand building and lead generation, simultaneously
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  Traditional marketing forces a choice: spend on brand (boards,
                  print, sponsorships) or spend on leads (portals, Google Ads).
                  Video advertising does both at once. Every listing video that
                  reaches a potential vendor also builds recognition for your
                  agency. After three to six months of consistent{" "}
                  <Link href="/guides/property-video-marketing" className="text-red underline underline-offset-2 hover:text-red/80">video marketing</Link>,
                  agents report that vendors approach them directly,
                  citing videos they have seen on social media.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Content that keeps working
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  A newspaper ad runs once and disappears. A leaflet goes in the
                  bin. A video lives on your social profiles indefinitely,
                  continuing to attract organic views long after any{" "}
                  <Link href="/guides/paid-video-ads-vs-organic-social-estate-agents" className="text-red underline underline-offset-2 hover:text-red/80">paid promotion</Link>{" "}
                  ends. One strong property video can generate
                  enquiries for weeks or months. This compound effect means your
                  effective CPL decreases over time as your library of content
                  grows.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Social proof at scale
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  Every view, like, share, and comment on your property videos is
                  public social proof. When a vendor considering three agents
                  checks your social media and sees thousands of views on your
                  listing videos, that is a powerful differentiator. No
                  traditional channel provides this kind of visible, quantifiable
                  credibility.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ROI calculator walkthrough */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              ROI breakdown
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Instructions won per £1,000 spent
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base text-slate">
              The ultimate measure of marketing ROI for an estate agent is
              instructions won. Here is what £1,000 typically delivers across
              each channel, from initial leads through to signed agreements.
            </p>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[700px] text-left">
                    <thead>
                      <tr className="border-b-2 border-stone">
                        <th className="pb-3 pr-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
                          Channel
                        </th>
                        <th className="pb-3 px-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
                          Spend
                        </th>
                        <th className="pb-3 px-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
                          Estimated leads
                        </th>
                        <th className="pb-3 px-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
                          Valuations
                        </th>
                        <th className="pb-3 pl-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
                          Instructions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ROI_DATA.map((row, i) => (
                        <tr
                          key={i}
                          className={`border-b border-stone/50 ${
                            row.channel === "Social video ads"
                              ? "bg-red/5"
                              : i % 2 === 0
                                ? "bg-cream/30"
                                : ""
                          }`}
                        >
                          <td className="py-3.5 pr-4 text-sm font-medium text-charcoal">
                            {row.channel}
                            {row.channel === "Social video ads" && (
                              <span className="ml-2 rounded-full bg-red/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red">
                                Best ROI
                              </span>
                            )}
                          </td>
                          <td className="py-3.5 px-4 text-sm text-slate">
                            {row.spend}
                          </td>
                          <td className="py-3.5 px-4 text-sm text-slate">
                            {row.estimatedLeads}
                          </td>
                          <td className="py-3.5 px-4 text-sm text-slate">
                            {row.valuations}
                          </td>
                          <td className="py-3.5 pl-4 text-sm font-semibold text-charcoal">
                            {row.instructions}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-6 text-xs leading-relaxed text-slate">
                  Estimates based on UK industry averages. Assumes a 12%
                  lead-to-valuation rate for video ads, 10% for portals, 8% for
                  Google, and 5-7% for print/leaflets. Instruction conversion
                  from valuation assumed at 25-30%. Your results will vary based
                  on your area, pricing, and presentation quality.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* QuiteRight callout */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <div className="rounded-2xl border-2 border-red/20 bg-red/5 p-8 md:p-12">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                The cost of content
              </p>
              <h2 className="mt-4 font-display text-3xl font-bold text-charcoal md:text-4xl">
                Professional video from £49/month
                <span className="text-red">.</span>
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate">
                The biggest barrier to video marketing has always been the cost
                of creating the content. Professional videography can cost
                £200-500 per property. QuiteRight changes the equation entirely.
              </p>
              <div className="mt-8 grid gap-6 sm:grid-cols-3">
                <div className="text-center">
                  <p className="font-display text-3xl font-bold text-charcoal">
                    &lt;£1
                  </p>
                  <p className="mt-1 text-sm font-semibold text-red">
                    Cost per video
                  </p>
                  <p className="mt-2 text-xs text-slate">
                    Unlimited videos on all plans. The more listings you market,
                    the lower your per-video cost.
                  </p>
                </div>
                <div className="text-center">
                  <p className="font-display text-3xl font-bold text-charcoal">
                    30s
                  </p>
                  <p className="mt-1 text-sm font-semibold text-red">
                    To create a video
                  </p>
                  <p className="mt-2 text-xs text-slate">
                    Paste a listing URL. QuiteRight does the rest. Professional
                    video ready for social media in under a minute.
                  </p>
                </div>
                <div className="text-center">
                  <p className="font-display text-3xl font-bold text-charcoal">
                    £49
                  </p>
                  <p className="mt-1 text-sm font-semibold text-red">
                    Per month
                  </p>
                  <p className="mt-2 text-xs text-slate">
                    Less than the cost of a single newspaper ad. Less than a
                    single for-sale board. Less than one day of portal fees.
                  </p>
                </div>
              </div>
              <div className="mt-8 text-center">
                <Link
                  href="/pricing"
                  className="inline-flex items-center gap-2 rounded-full bg-red px-8 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-red/90"
                >
                  See pricing plans
                  <svg
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    className="size-4"
                  >
                    <path d="M3 8h10M9 4l4 4-4 4" />
                  </svg>
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Common questions
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Frequently asked questions
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <FaqAccordion items={FAQ} />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Related guides */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Keep reading
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Related guides
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                title: "Property Video Marketing Guide",
                href: "/guides/property-video-marketing",
                description:
                  "Everything you need to know about using video to market property listings in 2026.",
              },
              {
                title: "Social Media for Estate Agents",
                href: "/guides/social-media-estate-agents",
                description:
                  "Build a social media strategy that wins instructions and grows your brand.",
              },
              {
                title: "Instagram vs Facebook vs TikTok",
                href: "/guides/instagram-vs-facebook-vs-tiktok-property-ads",
                description:
                  "Which social platform delivers the best results for property advertising?",
              },
            ].map((guide, i) => (
              <ScrollReveal key={i}>
                <Link
                  href={guide.href}
                  className="group flex h-full flex-col rounded-xl border border-stone bg-white p-6 transition hover:border-red/30 hover:shadow-sm"
                >
                  <h3 className="font-display text-base font-bold text-charcoal group-hover:text-red">
                    {guide.title}
                    <span className="text-red">.</span>
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate">
                    {guide.description}
                  </p>
                  <p className="mt-4 text-xs font-semibold text-red">
                    Read guide &rarr;
                  </p>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
              Ready to lower your cost per lead?
            </p>
            <h2 className="mt-4 font-display text-3xl font-bold text-white md:text-4xl">
              Start creating property videos today
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-white/70">
              Paste a listing. Get a scroll-stopping video. Share it on social
              media. Watch your cost per lead drop.{" "}
              <Link href="/how-it-works" className="text-red underline underline-offset-2 hover:text-red/80">See how it works</Link>.{" "}
              Professional property video
              marketing from under £1 per video.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Link
                href="/register"
                className="inline-flex items-center gap-2 rounded-full bg-red px-8 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-red/90"
              >
                Start free trial
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  className="size-4"
                >
                  <path d="M3 8h10M9 4l4 4-4 4" />
                </svg>
              </Link>
              <Link
                href="/pricing"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-3 text-sm font-semibold text-white transition hover:border-white/40"
              >
                View pricing
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  )
}
